import type { Account, CreditPack } from '@/api';
import { dollars } from '../format';

const FALLBACK_PACKS: CreditPack[] = [
  { id: 'starter', name: 'Starter', credits: 20, price_cents: 1000, blurb: 'About 20 hosted scores' },
  { id: 'builder', name: 'Builder', credits: 60, price_cents: 2500, blurb: 'About 60 scores, or 15 improvement loops' },
  { id: 'lab', name: 'Lab', credits: 150, price_cents: 5000, blurb: 'Volume refills, no SLA' },
];

export default function CreditsView({
  acc,
  packs,
  busy,
  err,
  note,
  onBuy,
  onSubscribe,
  onCancel,
}: {
  acc: Account;
  packs: CreditPack[];
  busy: boolean;
  err: string | null;
  note: string | null;
  onBuy: (pack: string) => void;
  onSubscribe: (plan: string) => void;
  onCancel: () => void;
}) {
  const list = packs.length ? packs : FALLBACK_PACKS;

  return (
    <div className="max-w-3xl">
      <h1 className="text-xl font-semibold text-textp">Hosting</h1>
      <p className="text-sm text-texts mt-1">
        New accounts start with 20 hosted credits so you can finish the site. When you are ready to
        go live, put a card on file. Hosting is monthly. Usage meters on the same invoice.
      </p>

      <div className="mt-4 inline-flex items-center gap-2 bg-surface border border-border rounded-full px-4 py-2">
        <i className="ri-flashlight-line text-accent text-sm" />
        <span className="text-sm text-texts">
          Current balance:
          <span className="ml-1 font-mono text-textp">{acc.credit_balance} credits</span>
        </span>
      </div>

      {err && (
        <div className="mt-4 text-sm text-error bg-error/10 border border-error/30 rounded-md px-3 py-2">{err}</div>
      )}
      {note && (
        <div className="mt-4 text-sm text-success bg-success/10 border border-success/30 rounded-md px-3 py-2">
          {note}
        </div>
      )}

      <section className="mt-6 bg-surface border border-accentLine rounded-xl p-5">
        <div className="text-xs uppercase tracking-wider text-texts">Hosting plan</div>
        <div className="mt-1 text-sm">
          Status: <span className="font-medium">{acc.subscription.status}</span>
          {acc.subscription.plan ? ` · ${acc.subscription.plan}` : ''}
        </div>
        <p className="mt-2 text-sm text-texts">
          Card on file. Monthly hosting plus metered usage on one invoice. Apply runs after payment.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          {acc.subscription.status !== 'active' ? (
            <button
              disabled={busy}
              onClick={() => onSubscribe('hosting')}
              className="flex items-center gap-1.5 bg-accent hover:bg-accent-hover disabled:opacity-60 text-accent-text text-sm px-3 py-1.5 rounded-md"
            >
              <i className="ri-rocket-line" /> Start hosting
            </button>
          ) : (
            <button
              disabled={busy}
              onClick={onCancel}
              className="flex items-center gap-1.5 bg-surface2 hover:bg-border disabled:opacity-60 text-textp text-sm px-3 py-1.5 rounded-md"
            >
              <i className="ri-close-circle-line" /> Cancel hosting
            </button>
          )}
        </div>
      </section>

      <div className="mt-8 text-xs uppercase tracking-wider text-texts">Optional credit packs</div>
      <p className="mt-1 text-sm text-texts">Packs stay available as a refill. They are not how you go live.</p>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {list.map((pack) => {
          const featured = pack.id === 'builder';
          return (
            <div
              key={pack.id}
              className={`relative rounded-xl p-5 ${
                featured ? 'bg-surface border border-accentLine' : 'bg-surface border border-border'
              }`}
            >
              {featured && (
                <span className="absolute -top-2.5 left-5 whitespace-nowrap text-xs font-medium text-accent-text bg-accent px-2.5 py-0.5 rounded-full">
                  Most popular
                </span>
              )}

              <h3 className="text-base font-semibold text-textp">{pack.name}</h3>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-3xl font-mono text-textp">{dollars(pack.price_cents)}</span>
              </div>
              <p className="mt-1 text-sm font-mono text-accent">{pack.credits} credits</p>
              <p className="mt-3 text-sm text-texts">{pack.blurb || ''}</p>

              <button
                disabled={busy}
                onClick={() => onBuy(pack.id)}
                className={`whitespace-nowrap mt-5 w-full text-sm font-medium px-4 py-2 rounded-lg transition-colors disabled:opacity-60 ${
                  featured
                    ? 'text-accent-text bg-accent hover:bg-accent-hover'
                    : 'text-textp bg-surface2 hover:bg-border border border-border'
                }`}
              >
                Buy {pack.name}
              </button>
            </div>
          );
        })}
      </div>

      <section className="mt-8 bg-surface border border-border rounded-xl p-5">
        <div className="text-xs uppercase tracking-wider text-texts mb-2">Recent transactions</div>
        {acc.transactions.length === 0 ? (
          <div className="text-texts text-sm">No transactions yet.</div>
        ) : (
          <ul className="divide-y divide-border">
            {acc.transactions
              .slice()
              .reverse()
              .map((t) => (
                <li key={t.id} className="py-2 flex items-center justify-between text-sm">
                  <span className="text-textp">{t.type.replace(/_/g, ' ')}</span>
                  <span className="font-mono text-texts">
                    {t.amount > 0 ? '+' : ''}
                    {t.amount}
                  </span>
                </li>
              ))}
          </ul>
        )}
      </section>

      <p className="mt-6 text-xs text-texts">Payments are processed by Stripe. Credits land after checkout completes.</p>
    </div>
  );
}
